import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { auth, db } from "./firebaseConfig";  // Import Firestore from firebaseConfig
import { FaChartPie, FaUserFriends, FaProjectDiagram, FaRegFileAlt, FaAppStore, FaPlug, FaCog, FaHome, FaSignOutAlt, FaUserCircle, FaCog as FaSettings, FaShieldAlt } from "react-icons/fa";
import HomePage from "./components/HomePage/HomePage";
import AppCatalog from "./components/AppCatalog/AppCatalog";
import ActivityAuditPage from "./components/ActivityAudit/ActivityAuditPage";
import TabInfo from './components/TabHeader/TabInfo';
import { HelmetProvider } from 'react-helmet-async';
import LoginPage from "./components/LoginPage/LoginPage";
import UserManagementPage from "./components/UserManagementPage/UserManagementPage";
import OrganizationSettings from "./components/OrganizationSettings/OrganizationSettings";
import OneLoginApp from "./components/applications/OneLoginApp";
import OneLoginCallback from "./components/applications/OneLoginCallback";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore"; // Import Firestore methods
import "./App.css";
import PersonalProfile from "./components/ProfileBubble/PersonalProfile";

// Placeholder component for pages under development
const Placeholder = ({ title }) => (
  <div style={{ padding: '20px' }}>
    <h2>{title}</h2>
    <p>This page is under development.</p>
  </div>
);

// Header Component
function Header() {
  return (
    <header className="header-bar">
      <NavLink to="/home" className="header-logo-link">
        <img 
          src="https://i.imgur.com/6874tCJ.png" 
          alt="CycleIT Logo" 
          style={{ width: '150px', height: '50px' }}
        />
      </NavLink>
    </header>
  );
}

// Sidebar Component
function Sidebar({ onSignOut, currentUser, role }) {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const handleDropdownToggle = () => setDropdownOpen(!isDropdownOpen);
  const handleAdminDropdownToggle = () => {
    setAdminDropdownOpen(!isAdminDropdownOpen);
    // If we're not in an admin route and we're closing the dropdown, remove active state
    if (!window.location.pathname.startsWith('/admin/') && isAdminDropdownOpen) {
      const adminLink = document.querySelector('.sidebar-dropdown .sidebar-link');
      if (adminLink) adminLink.classList.remove('active');
    }
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    setDropdownOpen(false);
    navigate('/settings');
  };

  // Function to check if any admin route is active
  const isAdminRouteActive = () => {
    const path = window.location.pathname;
    return path.startsWith('/admin/') || (isAdminDropdownOpen && !path.match(/^\/(home|insights|workflows|activity-audit|application-catalog|integrations|settings)/));
  };

  return (
    <div className="Sidebar">
      <NavLink to="/home" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaHome className="sidebar-icon" />
        Home
      </NavLink>
      <NavLink to="/insights" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaChartPie className="sidebar-icon" />
        Insights
      </NavLink>
      <NavLink to="/workflows" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaProjectDiagram className="sidebar-icon" />
        Workflows
      </NavLink>
      <NavLink to="/activity-audit" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaRegFileAlt className="sidebar-icon" />
        Activity Audit
      </NavLink>
      <NavLink to="/application-catalog" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaAppStore className="sidebar-icon" />
        App Catalog
      </NavLink>
      <NavLink to="/integrations" className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}>
        <FaPlug className="sidebar-icon" />
        Integrations
      </NavLink>

      {/* Admin Panel Dropdown */}
      <div className="sidebar-dropdown">
        <div 
          className={`sidebar-link ${isAdminRouteActive() ? 'active' : ''}`} 
          onClick={handleAdminDropdownToggle}
        >
          <div className="sidebar-link-content">
            <FaShieldAlt className="sidebar-icon" />
            Admin Panel
          </div>
          <span className="dropdown-indicator"></span>
        </div>
        {isAdminDropdownOpen && (
          <div className="sidebar-submenu">
            <NavLink to="/admin/user-management" className={({ isActive }) => (isActive ? "sidebar-sublink active" : "sidebar-sublink")}>
              <FaUserFriends className="sidebar-icon" />
              User Management
            </NavLink>
            <NavLink to="/admin/organization" className={({ isActive }) => (isActive ? "sidebar-sublink active" : "sidebar-sublink")}>
              <FaSettings className="sidebar-icon" />
              Organization Settings
            </NavLink>
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className="profile-section">
        <div className="profile-circle" onClick={handleDropdownToggle}>
          {currentUser.photoURL ? (
            <img 
              src={currentUser.photoURL || "https://via.placeholder.com/150"}
              alt="Profile" 
              className="profile-image"
            />
          ) : (
            <FaUserCircle size={30} />
          )}
        </div>
        {isDropdownOpen && (
          <div className="profile-dropdown">
            <div className="profile-dropdown-item" onClick={handleProfileClick}>
              <FaUserCircle style={{ marginRight: '8px' }} /> My Profile
            </div>
            <div className="profile-dropdown-item" onClick={handleSettingsClick}>
              <FaSettings style={{ marginRight: '8px' }} /> Settings
            </div>
            <div className="profile-dropdown-item" onClick={onSignOut}>
              <FaSignOutAlt style={{ marginRight: '8px' }} /> Sign Out
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);  // Reference to user's Firestore document
        const userSnapshot = await getDoc(userRef);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          setRole(userData.role || "Viewer");  // Set the role from Firestore
        } else {
          await setDoc(userRef, {
            displayName: user.displayName || "N/A",
            email: user.email || "N/A",
            role: "Viewer",  // Default role
            lastSignInTime: new Date(),  // Add last sign-in time
          }, { merge: true });  // Merge ensures fields are updated, not replaced
          setRole("Viewer");
        }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setRole(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = () => {
    auth.signOut().then(() => {
      setCurrentUser(null);
      setRole(null);
    }).catch((error) => {
      console.error("Error signing out: ", error);
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <LoginPage />;
  }

  return (
    <HelmetProvider>
      <TabInfo />
      <Router>
        <div className="App">
          <Header />
          <div className="Main-section">
            <Sidebar onSignOut={handleSignOut} currentUser={currentUser} role={role} />
            <div className="Content">
              {/* <h3>Welcome, {currentUser.displayName}</h3> */}

              <Routes>
                <Route path="/home" element={<HomePage />} />
                <Route path="/insights" element={<Placeholder title="Insights Page" />} />
                <Route path="/workflows" element={<Placeholder title="Workflows Page" />} />
                <Route path="/activity-audit" element={<ActivityAuditPage title="Activity Audit" />} />
                <Route path="/application-catalog" element={<AppCatalog title="App Catalog Page" />} />
                <Route path="/integrations" element={<Placeholder title="Integrations Page" />} />
                <Route path="/onelogin" element={<OneLoginApp />} />
                <Route path="/onelogin/callback" element={<OneLoginCallback />} />
                
                {/* Profile Route */}
                <Route 
                  path="/profile" 
                  element={
                    <PersonalProfile 
                      currentUser={currentUser} 
                      userRole={role}
                    />
                  } 
                />

                {/* Profile and Settings Routes (available to all users) */}
                <Route path="/settings" element={<Placeholder title="Settings Page" />} />

                {/* Admin Panel Routes */}
                {role === "Admin" ? (
                  <>
                    <Route path="/admin/user-management" element={<UserManagementPage />} />
                    <Route path="/admin/organization" element={<OrganizationSettings />} />
                  </>
                ) : (
                  <>
                    <Route path="/admin/*" element={<p>You must be an Admin to access this section.</p>} />
                  </>
                )}

                <Route path="/" element={<HomePage />} />
              </Routes>
            </div>
          </div>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;